import "./_style.less";
import { useTranslation } from "react-i18next";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import { Col, Row, Typography } from "antd";
import {
  VuiButton,
  VuiFormCard,
  VuiFormText,
  VuiNumberFormat,
} from "../../../../@vendor/components";
import { CounselingIcon, SessionDetailModal } from "../../../../components";
import { useAppSelector } from "../../../../stores/hooks";
import { useCallback, useMemo, useState } from "react";
import { AxiosError } from "axios";
import {
  formatDayDate,
  formatTimeValueBaseOnDuration,
  getTimeZone,
  handleErrorSaveDataResponse,
  usePassportService,
} from "../../../../@vendor/utils";
import CartRepository from "../../../../repositories/CartRepository";
import { useNavigate } from "react-router-dom";
import { useCheckoutBookingCounseling } from "../../providers/checkout-booking-counseling.provider";
import { Schedule } from "../../../../models/Schedule";

const { Text } = Typography;

const BookingCounselingStepPreview = () => {
  const { t } = useTranslation();
  const { currentStep, form, resetForm } = useBookingCounseling();
  const { onChangeBookingData } = useCheckoutBookingCounseling();
  const { values } = form;
  const { refetchUser } = usePassportService(true);
  const { account } = useAppSelector((state) => state.system);
  const isLogin = useMemo(() => !!account?.id, [account]);
  const [addCartLoading, setAddCartLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [showSchedule, setShowSchedule] = useState<boolean>(false);

  const price = useMemo(() => {
    let total = 0;
    if (currentStep === 2) {
      if (values?.session === "single-session") {
        total =
          values?.schedule_duration === 90
            ? values?.psychologist?.ninety_minutes_price
            : values?.psychologist?.sixty_minutes_price;
      } else if (values?.session === "five-session") {
        total = values?.psychologist?.sixty_minutes_price * 5;
      }

      if (values?.discount > 0) {
        total = total - total * (values?.discount / 100);
      }
    }
    return total;
  }, [
    values?.psychologist,
    values?.discount,
    values?.schedule_duration,
    currentStep,
  ]);

  const onAddToCart = useCallback(async () => {
    setAddCartLoading(true);

    const payload: any = {
      customer_id: values?.customer_id,
      category_id: values?.category_id,
    };

    if (values?.session !== "single-session") {
      payload.is_package = 1;
      payload.schedule_ids = values?.schedule_ids;
    } else {
      payload.schedule_id = values?.schedule_id ?? 0;
      payload.duration = values?.schedule_duration ?? 0;
    }

    await CartRepository.create(payload)
      .then(async (response) => {
        const { data: responseData } = response.data;
        await refetchUser();
        navigate(`/cart?carts=${responseData.id}`);
      })
      .catch((error: AxiosError) => {
        handleErrorSaveDataResponse(t, error);
      })
      .finally(() => {
        setAddCartLoading(false);
      });
  }, [values]);

  const onCheckout = useCallback(async () => {
    if (!isLogin) {
      const payload: any = {
        name: values?.patient?.name,
        email: values?.patient?.email,
        phone_number: values?.patient?.phone_number?.phone,
        phone_country: String(values?.patient?.phone_number?.code) ?? "62",
        date_of_birth: values?.patient?.date_of_birth,
        category_id: values?.category_id,
        patient: values?.patient,
        bookings: [],
        subtotal: price,
        total: price,
      };

      if (values?.session !== "single-session") {
        payload.bookings.push({
          category_id: values?.category_id,
          duration: 60,
          price: price,
          is_package: true,
          session_amount: values?.time_ids?.length,
          schedules: values?.time?.map((s: Schedule) => ({
            ...s,
            schedule_id: s?.id,
            duration: 60,
          })),
        });
      } else {
        payload.bookings.push({
          schedule_id: values?.schedule_id,
          category_id: values?.category_id,
          duration: values?.schedule_duration,
          price: price,
          is_package: false,
        });
      }

      onChangeBookingData(payload);
      navigate("/checkout-counseling");
      return;
    }

    const payload: any = {
      customer_id: values?.customer_id,
      category_id: values?.category_id,
    };

    if (values?.session !== "single-session") {
      payload.is_package = 1;
      payload.schedule_ids = values?.time_ids;
    } else {
      payload.schedule_id = values?.schedule_id ?? 0;
      payload.duration = values?.schedule_duration ?? 0;
    }

    await CartRepository.create(payload)
      .then(async (response) => {
        const { data: responseData } = response.data;
        await refetchUser();
        resetForm();
        navigate(`/cart?step=payment&carts=${responseData.id}`);
      })
      .catch((error: AxiosError) => {
        handleErrorSaveDataResponse(t, error);
      })
      .finally(() => {
        setAddCartLoading(false);
      });
  }, [values, isLogin, price]);

  if (currentStep !== 2) {
    return null;
  }

  return (
    <div className="booking-counseling-step-preview">
      <VuiFormCard>
        <Row gutter={[20, 20]}>
          <Col md={8} xs={24}>
            <div className="booking-counseling-psychologist-preview">
              <div className="booking-counseling-psychologist-image">
                <img
                  alt={values?.psychologist?.name}
                  src={values?.psychologist?.photo?.url}
                />
              </div>
              <div>
                <div className="booking-counseling-psychologist-name">
                  {values?.psychologist?.name}
                </div>
                {values?.psychologist?.title && (
                  <div
                    className="booking-counseling-psychologist-title"
                    dangerouslySetInnerHTML={{
                      __html: values?.psychologist?.title,
                    }}
                  />
                )}
              </div>
            </div>
          </Col>

          <Col md={16} xs={24}>
            <Row gutter={[20, 32]}>
              <Col md={24} xs={24}>
                <div className="booking-counseling-preview-type-wrapper">
                  <CounselingIcon
                    className="booking-counseling-preview-icon"
                    color="#FFA64C"
                  />
                  <div className="booking-counseling-preview-type">
                    {t("common.text.counselingAndPsychotherapy")}
                  </div>
                  <div className="booking-counseling-preview-session">
                    {values?.session === "single-session"
                      ? t("common.text.singleSession")
                      : t("common.text.fiveSessions")}
                  </div>
                </div>
              </Col>

              <Col md={12} xs={24}>
                <VuiFormText
                  label={t("common.form.category.label")}
                  value={values?.category?.name}
                  valueWeightType="bold"
                />
              </Col>
              <Col md={12} xs={24}>
                <VuiFormText
                  label={t("common.text.counselingAndPsychotherapyMethod")}
                  value={values?.type}
                  valueWeightType="bold"
                />
              </Col>
              <Col md={12} xs={24}>
                {values?.session === "single-session" ? (
                  <VuiFormText
                    label={t("common.text.dateAndTime")}
                    value={
                      <Text
                        style={{
                          fontWeight: 700,
                          color: "#ff00ae",
                        }}
                      >
                        {`${formatDayDate(
                          values?.schedule_date?.date || "",
                          "dddd, DD MMMM YYYY"
                        )} | ${formatTimeValueBaseOnDuration(
                          values?.schedule?.label,
                          values?.schedule_duration
                        )} (${getTimeZone()})`}
                      </Text>
                    }
                    valueWeightType="bold"
                  />
                ) : (
                  <VuiFormText
                    label={t("common.text.dateAndTime")}
                    value={
                      <Text
                        style={{
                          fontWeight: 700,
                          color: "#ff00ae",
                          cursor: "pointer",
                        }}
                        onClick={() => setShowSchedule(true)}
                      >
                        {t("common.text.viewScheduleDetails")}
                      </Text>
                    }
                    valueWeightType="bold"
                  />
                )}
              </Col>
              <Col md={12} xs={24}>
                {isLogin ? (
                  <VuiFormText
                    label={t("common.form.patient.label")}
                    value={values?.customer?.name}
                    valueWeightType="bold"
                  />
                ) : (
                  <VuiFormText
                    label={t("common.form.patient.label")}
                    value={values?.patient?.name}
                    valueWeightType="bold"
                  />
                )}
              </Col>

              <Col md={24} xs={24}>
                <div className="booking-preview-price-wrapper">
                  <div className="booking-preview-price-section">
                    <Text>{t("common.text.total")}</Text>
                    <Text>
                      <VuiNumberFormat
                        className="booking-preview-price"
                        value={price}
                      />
                      <div className="booking-preview-price-exclude-tax">
                        *{t("common.text.priceExcludeTax")}
                      </div>
                    </Text>
                  </div>
                  <div className="booking-preview-action-wrapper">
                    {isLogin && (
                      <VuiButton
                        buttonProps={{
                          type: "ghost",
                          onClick: () => {
                            onAddToCart().then();
                          },
                        }}
                        loading={addCartLoading}
                        label={t("common.button.addToCart")}
                      />
                    )}
                    <VuiButton
                      buttonProps={{
                        type: "primary",
                        onClick: () => {
                          onCheckout().then();
                        },
                      }}
                      label={t("common.button.directCheckout")}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </VuiFormCard>

      <SessionDetailModal
        data={values?.time || []}
        title={t("common.text.scheduleDetails")}
        visible={showSchedule}
        onClose={() => setShowSchedule(false)}
      />
    </div>
  );
};

export default BookingCounselingStepPreview;
